import type {
  ProductDetailResponse,
  ProductVariant,
  RawProductDetailResponse,
  RawProductVariant,
} from "@/src/types/product";
import { apiFetch, API_V2, MOBILE_API } from "./api";

/**
 * Map portal-api's old-format variants to the clean UI shape. The wire field
 * `productId` actually holds the ProductVariant Mongo _id (what the cart needs
 * as variantId), and `attributes` is the display label.
 */
function mapVariants(raw?: RawProductVariant[]): ProductVariant[] | undefined {
  if (!raw || raw.length === 0) return undefined;
  return raw.map((v, i) => ({
    variantId: String(v.productId),
    sku: v.sku,
    label: (v.attributes && v.attributes.trim()) || v.sku || `Option ${i + 1}`,
    thumbnail: v.thumbnail,
    imageUrls: v.imageUrls,
    mrp: v.mrp,
    sellingPrice: v.sellingPrice,
    stockQuantity: v.stockQuantity,
  }));
}

export async function getProductDetails(
  productId: string,
  outletId: string
): Promise<ProductDetailResponse> {
  const res = await apiFetch<RawProductDetailResponse>(
    `${API_V2}/product/product-details?productId=${productId}&outletId=${outletId}`
  );
  return {
    ...res,
    data: {
      ...res.data,
      product: {
        ...res.data.product,
        variants: mapVariants(res.data.product.variants),
      },
    },
  };
}

export interface SearchResult {
  id: string;       // MongoDB _id from grablocal-service-product
  name: string;
  thumbnail?: string;
  mrpPrice?: string;
  sellingPrice?: string;
  stockQuantity?: number;
  /**
   * The branch this result belongs to. A multi-branch store's grid can show a
   * card held by any branch, so navigation must carry the product's OWN outlet
   * — stamping the header's context outlet would open the product detail page
   * against a branch that does not stock it.
   */
  outletId?: string;
}

interface RawSearchProduct {
  id: string;
  name: string;
  thumbnail?: string | null;
  mrpPrice?: string;
  sellingPrice?: string;
  stockQuantity?: number;
}

export async function searchProducts(
  query: string,
  outletId: string,
  limit = 8
): Promise<SearchResult[]> {
  const params = new URLSearchParams({ query, outletId, limit: String(limit) });
  // portal-api search-product-in-store now proxies to grablocal-service-product
  // returns MongoDB _id as `id` field — correct for product detail navigation
  const res = await apiFetch<{
    success: boolean;
    data: RawSearchProduct[];
  }>(`${MOBILE_API}/product/search-product-in-store?${params}`);

  const raw = Array.isArray(res.data) ? res.data : [];
  return raw.map((p) => ({
    id: String(p.id),
    name: p.name,
    thumbnail: p.thumbnail ?? undefined,
    mrpPrice: p.mrpPrice,
    sellingPrice: p.sellingPrice,
    stockQuantity: p.stockQuantity,
    outletId,
  }));
}

/** One row of `GET /v3/buyer/search`, as shaped by the service's variant card. */
export interface RawSearchCard {
  productId: string;
  name: string;
  tenantId?: string;
  outletId?: string;
  outlet?: { outletId?: string } | null;
  category?: { _id?: string } | null;
  thumbnail?: string | null;
  pricing?: { mrp?: number | null; sellingPrice?: number | null };
  stockQuantity?: number;
}

const money = (n?: number | null) => (typeof n === "number" ? n.toFixed(2) : undefined);

/**
 * Shape raw `/v3/buyer/search` rows into header results.
 *
 * Deliberately a pure mapping — no grouping. The product service already
 * collapses a seller's cross-branch clones (`utils/uniqueProducts`) and just as
 * deliberately keeps one card PER VARIANT, so "Coffee 250g" and "Coffee 1kg"
 * are two cards. Re-grouping here would make the typeahead disagree with the
 * grid it opens, and would put a second copy of the rule in a second repo where
 * the two could drift.
 *
 * Exported so the outlet resolution and price formatting stay testable without
 * a network.
 */
export function reconcileSearchCards(items: RawSearchCard[], limit: number): SearchResult[] {
  return items.slice(0, limit).map((raw) => ({
    id: String(raw.productId),
    name: raw.name,
    thumbnail: raw.thumbnail ?? undefined,
    mrpPrice: money(raw.pricing?.mrp),
    sellingPrice: money(raw.pricing?.sellingPrice),
    stockQuantity: raw.stockQuantity,
    /* The row's OWN branch. Search spans every branch now, so stamping the
     * header's context outlet would open a product against a branch that does
     * not stock it. */
    outletId: raw.outlet?.outletId ?? raw.outletId,
  }));
}

/**
 * Header search across every branch a store runs.
 *
 * The old path (`search-product-in-store`) requires a single `outletId`, so on a
 * multi-branch store a shopper could see a product in the grid, type its name,
 * and get nothing back — the grid is grouped across branches now, the search box
 * was not. `GET /v3/buyer/search` already accepts `tenantId`, so this scopes to
 * the whole store and reconciles the results client-side.
 *
 * Two dedupe passes are needed and they are not the same pass:
 *
 *   1. by `productId` — the endpoint returns one row PER VARIANT, so a product
 *      with three sizes arrives three times under one name.
 *   2. by group — the same product stocked at two branches is two documents.
 *
 * Overfetches because both passes shrink the list, then trims to `limit`.
 * Falls back to the outlet-scoped endpoint when there is no tenantId or the
 * request fails, so a single-branch store and an older deploy both keep working.
 */
export async function searchProductsForStore(
  query: string,
  scope: { tenantId?: string; outletId: string },
  limit = 8
): Promise<SearchResult[]> {
  const root = process.env.NEXT_PUBLIC_PRODUCT_SERVICE_URL?.replace(/\/+$/, "");
  if (!root || !scope.tenantId) return searchProducts(query, scope.outletId, limit);

  try {
    const params = new URLSearchParams({
      q: query,
      tenantId: scope.tenantId,
      limit: String(limit),
    });
    const res = await fetch(`${root}/v3/buyer/search?${params}`, {
      cache: "no-store",
      headers: { accept: "application/json" },
    });
    if (!res.ok) return searchProducts(query, scope.outletId, limit);

    const body = await res.json();
    const items: RawSearchCard[] = Array.isArray(body?.data?.items) ? body.data.items : [];
    if (!items.length) return [];

    return reconcileSearchCards(items, limit);
  } catch {
    // Network blip or an older service — the outlet-scoped path still answers.
    return searchProducts(query, scope.outletId, limit);
  }
}
